import { v4 as uuid } from 'uuid'
import { CampaignOption } from 'types'
import { Schedule } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { ContentCategory, CONTENT_CATEGORY_CONFIG } from 'utils/brandPlans/contentCategories'
import {
  computePostTimes,
  expandTopicRowsToPayload,
  getDefaultTacticsForPostType,
  POST_TYPE_TO_CONTENT_TYPE,
  storyApiTypeFromTactics,
} from '../CreateContentModal/utils'
import { ComposeRowData } from './Types'
import { isVideoScriptCategory, withVideoScriptPayload } from 'utils/videoScriptRequest'

const DEFAULT_CATEGORY: ContentCategory = 'still_image_post'

const newRow = (
  schedule: CampaignOption,
  category: ContentCategory,
  postAt: ComposeRowData['postAt'],
  isBlank: boolean,
): ComposeRowData => {
  const config = CONTENT_CATEGORY_CONFIG[category]
  return {
    id: uuid(),
    topic: '',
    postType: config.postType,
    contentCategory: category,
    tactics: getDefaultTacticsForPostType(config.postType, schedule.tactics || []),
    postAt,
    mediaFileIds: [],
    isBlank,
    referenceMedia: null,
    contextSources: [],
    loading: !isBlank,
    userEdited: false,
    avatar: null,
    avatarAngle: 'testimonial',
    avatarScript: '',
    avatarCaptions: true,
    avatarCaptionStyle: null,
    productImage: null,
    videoScript: '',
    videoVoiceoverVoice: null,
  }
}

export const buildInitialComposeRow = (
  schedule: CampaignOption,
  isBlank: boolean,
  category: ContentCategory = DEFAULT_CATEGORY,
): ComposeRowData => {
  const [postAt] = computePostTimes(schedule, 1)
  return newRow(schedule, category, postAt, isBlank)
}

export const appendComposeRow = (
  schedule: CampaignOption,
  existing: ComposeRowData[],
  isBlank: boolean,
): ComposeRowData => {
  const times = computePostTimes(schedule, existing.length + 1)
  const postAt = times[existing.length] ?? times[times.length - 1]
  return newRow(schedule, DEFAULT_CATEGORY, postAt, isBlank)
}

const contextSourceIds = (row: ComposeRowData): number[] | undefined => {
  const ids = row.contextSources?.map((cs) => cs.contextSourceId) ?? []
  return ids.length > 0 ? ids : undefined
}

const blankRowToPayload = (row: ComposeRowData): Schedule => {
  const config = CONTENT_CATEGORY_CONFIG[row.contentCategory]
  const type =
    row.contentCategory === 'stories'
      ? storyApiTypeFromTactics(row.tactics)
      : (config.apiType as Schedule['type']) ?? POST_TYPE_TO_CONTENT_TYPE[row.postType]
  return {
    type,
    topic: '',
    caption: row.topic,
    post_at: row.postAt,
    initial_media_file_ids: row.mediaFileIds,
    tactics:
      (row.postType === 'social_post' || row.postType === 'video') && row.tactics.length > 0 ? row.tactics : undefined,
    content_type: config.contentType,
    is_blank: true,
    context_source_ids: contextSourceIds(row),
  }
}

const withAvatarPayload = (payload: Schedule, row: ComposeRowData): Schedule => ({
  ...payload,
  avatar_id: row.avatar?.id,
  avatar_angle: row.avatarAngle,
  avatar_script: row.avatarScript || undefined,
  avatar_captions: row.avatarCaptions,
  avatar_caption_style: row.avatarCaptionStyle || undefined,
  initial_media_file_ids: row.productImage ? [String(row.productImage.id)] : undefined,
})

export const expandComposeRowsToPayload = (rows: ComposeRowData[]): Schedule[] => {
  return rows.map((row) => {
    if (row.isBlank) return blankRowToPayload(row)
    const [aiPayload] = expandTopicRowsToPayload([row])
    const payload: Schedule = { ...aiPayload, is_blank: false, context_source_ids: contextSourceIds(row) }
    if (row.contentCategory === 'avatar_video') return withAvatarPayload(payload, row)
    if (isVideoScriptCategory(row.contentCategory)) return withVideoScriptPayload(payload, row)
    return payload
  })
}
